import React, { useState, useRef, useEffect, useId, useMemo } from "react";
import Arrow from "src/assets/icons/arrowDown.svg";
import styles from "./styles.module.css";
import cx from "classnames";

type Option = {
  id: string | number;
  label: string;
};

type Props = {
  items: Option[];
  selectedId?: string | number | null;
  onSelect: (id: string | number | null) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  selectedClassName?: string;
  menuClassName?: string;
  isTop?: boolean;
  icon?: string;
  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  noResultsText?: string;
};

export const Select: React.FC<Props> = ({
  items,
  selectedId,
  onSelect,
  placeholder = "Select an option",
  label,
  className = "",
  selectedClassName = "",
  menuClassName = "",
  isTop,
  icon,
  error,
  disabled = false,
  clearable = false,
  searchable = false,
  noResultsText = "No options found",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const selectedItem = items.find((item) => item.id === selectedId);
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!searchable || !query) return items;

    return items.filter((item) => item.label.toLowerCase().includes(query));
  }, [items, searchQuery, searchable]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        setSearchQuery("");
      }
      return next;
    });
  };

  const handleItemClick = (id: string | number) => {
    if (disabled) return;
    onSelect(id);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleDropdown();
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  const handleItemKeyDown = (
    event: React.KeyboardEvent,
    id: string | number | null,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSelect(id);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div className={cx(styles.dropdown, className)} ref={dropdownRef}>
      {label && <div className={styles.label}>{label}</div>}
      <div
        className={cx(styles.selected, selectedClassName, {
          [styles.selectedError]: error,
          [styles.disabled]: disabled,
        })}
        onClick={toggleDropdown}
        onKeyDown={handleTriggerKeyDown}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {icon && <img src={icon} className={styles.iconImg} alt="Icon" />}
        {searchable && isOpen ? (
          <input
            autoFocus
            className={styles.searchInput}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            placeholder={selectedItem ? selectedItem.label : placeholder}
          />
        ) : (
          <span className={!selectedItem ? styles.placeholder : ""}>
            {selectedItem ? selectedItem.label : placeholder}
          </span>
        )}
        <div className={styles.arrows}>
          <img
            src={Arrow}
            alt="Arrow"
            className={disabled ? styles.disabledArrow : ""}
          />
        </div>
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      {isOpen && !disabled && (
        <div
          id={menuId}
          role="listbox"
          className={cx(styles.menu, menuClassName, {
            [styles.menuTop]: isTop,
            [styles.borderPath]: !!icon,
          })}
        >
          {clearable && selectedId != null && (
            <div
              className={cx(styles.item, styles.clearItem)}
              onClick={() => {
                onSelect(null);
                setIsOpen(false);
                setSearchQuery("");
              }}
              role="option"
              aria-selected={false}
              tabIndex={0}
              onKeyDown={(event) => handleItemKeyDown(event, null)}
            >
              Clear
            </div>
          )}
          {filteredItems.length === 0 && (
            <div className={cx(styles.item, styles.emptyItem)}>
              {noResultsText}
            </div>
          )}
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className={cx(styles.item, {
                [styles.itemSelected]: selectedId === item.id,
                [styles.disabledItem]: disabled,
              })}
              onClick={() => handleItemClick(item.id)}
              role="option"
              aria-selected={selectedId === item.id}
              tabIndex={0}
              onKeyDown={(event) => handleItemKeyDown(event, item.id)}
            >
              {item.label}
              {selectedId === item.id && (
                <svg
                  className={styles.checkmark}
                  width="16"
                  height="12"
                  viewBox="0 0 16 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 6L5.66667 11L15 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
